import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  cn,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nexus/ui";
import type { ChangelogEntry } from "@/features/changelog/api/changelog.api";
import {
  canApprove,
  canSend,
  ENTRY_CHANNELS,
  findDelivery,
  getEntryState,
  getSectionCounts,
  getTimestampDisplay,
  isDeployPickable,
} from "@/features/changelog/lib/changelog-display";
import { ChangelogStatusBadge } from "./shared/ChangelogStatusBadge";

interface ChangelogDeploysTableProps {
  entries: ChangelogEntry[];
  selectedTag: string | null;
  /** Roundup title per version, or null while roundups are unreadable. */
  roundupTitleByVersion: ReadonlyMap<string, string> | null;
  canApproveEntries: boolean;
  canSendEntries: boolean;
  /** Shows the picking column when the admin can compose a roundup or bulk-post. */
  canCompose: boolean;
  /** Versions a roundup could still take, keyed by tag. */
  composableVersions: ReadonlySet<string>;
  pickedVersions: ReadonlySet<string>;
  isBusy: boolean;
  onSelect: (entry: ChangelogEntry) => void;
  onApprove: (entry: ChangelogEntry) => void;
  onOpenSend: (entry: ChangelogEntry) => void;
  onTogglePick: (entry: ChangelogEntry) => void;
  onTogglePickAll: () => void;
}

const MONO_MICRO_LABEL = "font-mono text-[10px] text-muted-foreground";

/**
 * Where a deploy has reached and when it got there. A deploy only ever goes to
 * Slack under its own name, so the channel is named in words rather than given
 * an icon of its own — and the roundup line below it is the other half of the
 * answer, since that is how the same notes reach everyone outside engineering.
 */
const CHANNEL_LABEL: Record<"IN_APP" | "SLACK" | "EMAIL", string> = {
  IN_APP: "In-app",
  SLACK: "Slack",
  EMAIL: "Email",
};

function PublishedCell({
  entry,
  roundupTitle,
}: {
  entry: ChangelogEntry;
  roundupTitle: string | null;
}) {
  const { t, i18n } = useTranslation();
  const inApp = findDelivery(entry, "IN_APP");
  const slack = findDelivery(entry, "SLACK");
  const posted = [inApp, slack].filter(
    (delivery): delivery is NonNullable<typeof delivery> =>
      delivery !== undefined &&
      (delivery.status === "SENT" ||
        delivery.status === "SENDING" ||
        delivery.isStuck ||
        delivery.status === "FAILED"),
  );

  return (
    <div className="min-w-0">
      {posted.length === 0 ? (
        <div className="text-muted-foreground">
          {t("admin.changelog.table.notPublished", "Not posted")}
        </div>
      ) : (
        posted.map((delivery) => {
          const sentAt = delivery.sentAt
            ? getTimestampDisplay(delivery.sentAt, i18n.language)
            : null;
          const by = delivery.requestedByName;
          const channelLabel = t(
            `admin.changelog.channels.${delivery.channel}`,
            CHANNEL_LABEL[delivery.channel],
          );

          if (delivery.isStuck || delivery.status === "FAILED") {
            return (
              <div key={delivery.channel} className="text-error">
                {channelLabel}
                {" · "}
                {t("admin.changelog.table.slackFailed", "Failed")}
              </div>
            );
          }

          if (delivery.status === "SENDING") {
            return (
              <div key={delivery.channel} className="text-muted-foreground">
                {channelLabel}
                {" · "}
                {t("admin.changelog.table.slackSending", "Sending…")}
              </div>
            );
          }

          return (
            <div key={delivery.channel} className="min-w-0">
              <div className="truncate text-foreground">
                {channelLabel}
                {by ? (
                  <span className="text-muted-foreground">{` · ${by}`}</span>
                ) : null}
              </div>
              {sentAt ? (
                <div className="truncate text-[11px] tabular-nums text-muted-foreground">
                  {`${sentAt.time} ${sentAt.date}`}
                </div>
              ) : null}
            </div>
          );
        })
      )}

      {roundupTitle ? (
        <div className="truncate text-[11px] text-muted-foreground" title={roundupTitle}>
          {t("admin.changelog.table.inRoundup", "In {{title}}", { title: roundupTitle })}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Why a deploy cannot go into a roundup. `null` means it can. A tag missing
 * from the composable set with none of these explanations is one the API has
 * simply not offered — say nothing rather than guess at a reason.
 */
function pickBlockedReason(entry: ChangelogEntry, roundupTitle: string | null): string | null {
  if (roundupTitle !== null) return "alreadyInRoundup";
  if (entry.status !== "READY") return "notReady";
  if (entry.itemCount === 0) return "nothingUserFacing";
  return null;
}

export function ChangelogDeploysTable({
  entries,
  selectedTag,
  roundupTitleByVersion,
  canApproveEntries,
  canSendEntries,
  canCompose,
  composableVersions,
  pickedVersions,
  isBusy,
  onSelect,
  onApprove,
  onOpenSend,
  onTogglePick,
  onTogglePickAll,
}: ChangelogDeploysTableProps) {
  const { t, i18n } = useTranslation();

  const canPickColumn = canCompose || canSendEntries;
  const pickableOnScreen = entries.filter((row) =>
    isDeployPickable(row, { canCompose, canSendEntries, composableVersions }),
  );
  const isAllPicked =
    pickableOnScreen.length > 0 &&
    pickableOnScreen.every((row) => pickedVersions.has(row.versionTag));

  return (
    <Table className="table-fixed">
      <colgroup>
        {canPickColumn ? <col className="w-10" /> : null}
        <col className="w-[132px]" />
        <col className="w-[104px]" />
        <col />
        <col className="w-[136px]" />
        <col className="w-[104px]" />
        <col className="w-[112px]" />
        <col className="w-9" />
      </colgroup>
      <TableHeader className="sticky top-0 z-10">
        <TableRow className="hover:bg-muted">
          {canPickColumn ? (
            <TableHead className="px-2">
              <Checkbox
                checked={isAllPicked}
                disabled={pickableOnScreen.length === 0}
                aria-label={t(
                  "admin.changelog.table.pickAll",
                  "Pick every listed release",
                )}
                onCheckedChange={onTogglePickAll}
              />
            </TableHead>
          ) : null}
          <TableHead>{t("admin.changelog.table.version", "Version")}</TableHead>
          <TableHead>{t("admin.changelog.table.state", "State")}</TableHead>
          <TableHead>{t("admin.changelog.table.notes", "Release notes")}</TableHead>
          <TableHead>{t("admin.changelog.table.published", "Published")}</TableHead>
          <TableHead>{t("admin.changelog.table.deployed", "Released")}</TableHead>
          <TableHead />
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry) => {
          const state = getEntryState(entry);
          const deployed = getTimestampDisplay(entry.deployedAt, i18n.language);
          const counts = getSectionCounts(entry.sections);
          const roundupTitle = roundupTitleByVersion?.get(entry.versionTag) ?? null;
          const isComposable = composableVersions.has(entry.versionTag);
          const isPickable = isDeployPickable(entry, {
            canCompose,
            canSendEntries,
            composableVersions,
          });
          const blockedReason = pickBlockedReason(entry, roundupTitle);

          return (
            <TableRow
              key={entry.versionTag}
              onClick={() => onSelect(entry)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect(entry);
                }
              }}
              tabIndex={0}
              aria-label={t("admin.changelog.table.rowLabel", "Open notes for {{version}}", {
                version: entry.versionTag,
              })}
              data-state={selectedTag === entry.versionTag ? "selected" : undefined}
              className="cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
            >
              {canPickColumn ? (
                <TableCell
                  className="px-2"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <Checkbox
                    checked={pickedVersions.has(entry.versionTag)}
                    disabled={!isPickable || isBusy}
                    title={
                      isComposable || blockedReason === null
                        ? undefined
                        : t(`admin.changelog.table.pickBlocked.${blockedReason}`, {
                            title: roundupTitle ?? "",
                          })
                    }
                    aria-label={t("admin.changelog.table.pick", "Pick {{version}}", {
                      version: entry.versionTag,
                    })}
                    onCheckedChange={() => onTogglePick(entry)}
                  />
                </TableCell>
              ) : null}

              <TableCell className="whitespace-nowrap">
                <div className="truncate font-mono text-xs" title={entry.versionTag}>
                  {entry.versionTag}
                </div>
                <div className={cn(MONO_MICRO_LABEL, "truncate")} title={entry.branch}>
                  {entry.shortSha}
                </div>
              </TableCell>

              <TableCell>
                <ChangelogStatusBadge state={state} />
              </TableCell>

              <TableCell className="min-w-0">
                <div className="flex min-w-0 items-center gap-1.5">
                  {/* Only big news gets a mark: small update is the default,
                      and labelling every row with it would say nothing. */}
                  {entry.significance === "BIG_NEWS" ? (
                    <span className="shrink-0 rounded-sm bg-primary/10 px-1.5 py-px text-[10px] font-semibold text-primary">
                      {t("admin.changelog.significance.BIG_NEWS", "Big news")}
                    </span>
                  ) : null}
                  <div className="min-w-0 truncate" title={entry.headline ?? undefined}>
                    {entry.headline ?? (
                      <span className="text-muted-foreground">
                        {state === "generating" || state === "pending"
                          ? t("admin.changelog.table.writing", "Writing notes…")
                          : state === "stalled"
                            ? t("admin.changelog.table.stalled", "Never finished — open to retry")
                            : t("admin.changelog.table.noHeadline", "No headline")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {counts.length > 0
                    ? counts
                        .map(({ key, count }) =>
                          t(`admin.changelog.table.count.${key}`, {
                            count,
                            defaultValue: `${count} ${key}`,
                          }),
                        )
                        .join(" · ")
                    : entry.status === "READY"
                      ? t("admin.changelog.table.nothingUserFacing", "Nothing user-facing")
                      : ""}
                </div>
              </TableCell>

              <TableCell>
                <PublishedCell entry={entry} roundupTitle={roundupTitle} />
              </TableCell>

              <TableCell className="whitespace-nowrap">
                <div className="tabular-nums">{deployed.time}</div>
                <div className="text-[11px] text-muted-foreground">{deployed.date}</div>
              </TableCell>

              {/* The row opens the sheet, so the actions inside it keep their
                  clicks and keys to themselves. */}
              <TableCell
                className="px-1"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <div className="flex justify-end gap-1">
                  {canApproveEntries && canApprove(entry) ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-sm px-2 text-[11px]"
                      disabled={isBusy}
                      onClick={() => onApprove(entry)}
                    >
                      {t("admin.changelog.approve", "Approve")}
                    </Button>
                  ) : null}
                  {canSendEntries && canSend(entry, ENTRY_CHANNELS) ? (
                    <Button
                      size="sm"
                      className="h-7 rounded-sm px-2 text-[11px]"
                      disabled={isBusy}
                      onClick={() => onOpenSend(entry)}
                    >
                      {t("admin.changelog.table.post", "Post")}
                    </Button>
                  ) : null}
                </div>
              </TableCell>

              <TableCell className="px-2 text-muted-foreground">
                <Icon name="chevron-right" size="xs" color="inherit" aria-hidden />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
